import {
  BarChart3,
  CalendarDays,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  FileText,
  Filter,
  Plus,
  RefreshCw,
  Search,
  Truck,
  Users,
  Wrench,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import type { TenantSummary } from '@forq/contracts';

interface ProfessionalModulePreviewProps {
  readonly moduleId: string;
  readonly tenant: TenantSummary;
  readonly mode: 'demo' | 'supabase';
}

type ServiceView = 'worklist' | 'planner';
type RentalView = 'overview' | 'orders' | 'dispatch' | 'calendar' | 'documents';
type FinanceView = 'cockpit' | 'billing' | 'opos' | 'incoming' | 'analytics' | 'accounting' | 'closing';

const serviceOrders = [
  { id: 'AU-26-000024', type: 'Reparatur', customer: 'Frederick Befeld', machine: 'Gerät noch nicht bekannt', date: '14.07.2026 · 08:00', technician: 'Nicht geplant', status: 'Bereit zur Rechnungslegung', billing: 'Rechnung erstellt', tone: 'ready' },
  { id: 'AU-26-000012', type: 'Reparatur', customer: 'Mister Druck', machine: 'Linde E18-01', date: '14.07.2026 · 09:00', technician: 'Nicht geplant', status: 'Bereit zur Rechnungslegung', billing: 'Nicht abgerechnet', tone: 'ready' },
  { id: 'AU-26-000009', type: 'Reparatur', customer: 'Jürg Hagedorn', machine: 'Toyota 8FBE16T', date: '15.07.2026 · 08:00', technician: 'g.kaiser', status: 'Geplant', billing: 'Nicht abgerechnet', tone: 'planned' },
  { id: 'AU-26-000007', type: 'Reparatur', customer: 'Hans Meiser', machine: 'BT RRE160', date: '15.07.2026 · 15:30', technician: 'k.befeld', status: 'Unterlagen zurück', billing: 'Nicht abgerechnet', tone: 'attention' },
  { id: 'AU-26-000006', type: 'Wartung', customer: 'Kramer Logistik GmbH', machine: 'Jungheinrich EFG216', date: '16.07.2026 · 07:30', technician: 'm.juckenborg', status: 'Warten auf Teile', billing: 'Nicht abgerechnet', tone: 'attention' },
];

const rentalOrders = [
  { id: 'MI-26-000001', customer: 'Hans Meiser', machine: 'Toyota 8FBE16T', period: '07.07.2026 – 14.07.2026', status: 'Reserviert' },
  { id: 'MI-26-000002', customer: 'Test GmbH', machine: 'BT SWE 160', period: '08.07.2026 – 15.07.2026', status: 'Vermietet' },
  { id: 'MI-26-000003', customer: 'KETTLER Home&Garden GmbH', machine: 'Kärcher B 60 W Bp Pack', period: '24.07.2026 – 01.01.2027', status: 'Reserviert' },
];

function ModuleHeader({
  eyebrow,
  title,
  description,
  tenant,
  mode,
  actions,
}: {
  readonly eyebrow: string;
  readonly title: string;
  readonly description: string;
  readonly tenant: TenantSummary;
  readonly mode: 'demo' | 'supabase';
  readonly actions: React.ReactNode;
}) {
  return (
    <header className="professional-module__header">
      <div>
        <span>{eyebrow}</span>
        <h1>{title}</h1>
        <p>{description}</p>
      </div>
      <div className="professional-module__header-actions">
        <small>{tenant.shortName} · {mode === 'demo' ? 'Vorschau' : 'Supabase'}</small>
        {actions}
      </div>
    </header>
  );
}

function ServicePreview({ tenant, mode }: Omit<ProfessionalModulePreviewProps, 'moduleId'>) {
  const [view, setView] = useState<ServiceView>('worklist');
  const [selectedId, setSelectedId] = useState(serviceOrders[0].id);
  const selected = serviceOrders.find((order) => order.id === selectedId) ?? serviceOrders[0];

  return (
    <div className="professional-module">
      <ModuleHeader
        eyebrow="FORQ Service"
        title="Service & Werkstatt"
        description="Aufträge bearbeiten, Unterlagen prüfen und Einsätze planen – ohne vorgeschaltetes Dashboard."
        tenant={tenant}
        mode={mode}
        actions={(
          <>
            <button type="button" className={view === 'worklist' ? 'is-active' : ''} onClick={() => setView('worklist')}><ClipboardList size={14} /> Arbeitsliste</button>
            <button type="button" className={view === 'planner' ? 'is-active' : ''} onClick={() => setView('planner')}><CalendarDays size={14} /> Serviceplaner</button>
            <button type="button" className="is-primary"><Plus size={14} /> Serviceauftrag planen</button>
          </>
        )}
      />

      {view === 'worklist' ? (
        <>
          <div className="erp-filter-tabs">
            <button type="button" className="is-active">Alle offenen <b>5</b></button>
            <button type="button">Planung offen <b>1</b></button>
            <button type="button">Unterlagen zurück <b>1</b></button>
            <button type="button">Bereit zur Rechnung <b>2</b></button>
            <button type="button">Warten auf Teile <b>1</b></button>
          </div>
          <div className="erp-toolbar">
            <label><Search size={14} /><input placeholder="Auftrag, Kunde, Maschine oder Monteur suchen" /></label>
            <button type="button"><Filter size={14} /> Filter</button>
            <button type="button">Spalten</button>
            <span>{serviceOrders.length} Datensätze</span>
          </div>
          <div className="erp-split-workspace">
            <section className="erp-table-panel">
              <div className="erp-table erp-service-table">
                <div className="erp-table__head"><span>Auftrag</span><span>Art</span><span>Kunde</span><span>Maschine</span><span>Termin</span><span>Monteur</span><span>Status</span><span>Abrechnung</span></div>
                {serviceOrders.map((order) => (
                  <button type="button" className={`erp-table__row ${selectedId === order.id ? 'is-selected' : ''}`} key={order.id} onClick={() => setSelectedId(order.id)}>
                    <span><strong>{order.id}</strong></span><span>{order.type}</span><span>{order.customer}</span><span>{order.machine}</span><span>{order.date}</span><span>{order.technician}</span><span><i className={`is-${order.tone}`} />{order.status}</span><span>{order.billing}</span>
                  </button>
                ))}
              </div>
            </section>
            <aside className="erp-detail-panel">
              <div className="erp-detail-panel__heading">
                <div><span>{selected.id}</span><h2>{selected.type} · {selected.machine}</h2></div>
                <em className={`is-${selected.tone}`}>{selected.status}</em>
              </div>
              <nav><button type="button" className="is-active">Übersicht</button><button type="button">Positionen</button><button type="button">Unterlagen</button><button type="button">Verlauf</button></nav>
              <dl>
                <div><dt>Kunde</dt><dd>{selected.customer}</dd></div>
                <div><dt>Maschine</dt><dd>{selected.machine}</dd></div>
                <div><dt>Termin</dt><dd>{selected.date}</dd></div>
                <div><dt>Monteur</dt><dd>{selected.technician}</dd></div>
              </dl>
              <section><span>Geplanter Arbeitsauftrag</span><strong>Gerät prüfen, Fehler feststellen und Reparatur durchführen.</strong></section>
              <div className="erp-detail-panel__actions"><button type="button" className="is-primary"><Wrench size={14} /> Serviceakte öffnen</button><button type="button"><FileText size={14} /> Unterlagen</button></div>
            </aside>
          </div>
        </>
      ) : (
        <section className="erp-planner">
          <header><div><span>Serviceplaner</span><h2>13.07.2026 – 19.07.2026</h2></div><div><button type="button"><ChevronLeft size={14} /></button><button type="button">Heute</button><button type="button"><ChevronRight size={14} /></button><button type="button" className="is-primary"><Plus size={14} /> Einsatz planen</button></div></header>
          <div className="erp-planner__body">
            <aside><span>Disposition</span><h3>Ungeplante Aufträge</h3><article><strong>AU-26-000006</strong><small>Hans Meiser · Reparatur</small></article></aside>
            <div className="erp-planner__grid">
              <div className="erp-planner__head"><span>Monteur</span>{['Mo, 13.07.', 'Di, 14.07.', 'Mi, 15.07.', 'Do, 16.07.', 'Fr, 17.07.'].map((day) => <span key={day}>{day}</span>)}</div>
              {['f.befeld', 'f.brueno', 'g.kaiser', 'k.befeld', 'm.juckenborg'].map((person, index) => (
                <div className="erp-planner__row" key={person}><strong>{person}</strong>{[0, 1, 2, 3, 4].map((day) => <span key={day}>{day === index % 5 ? <i>{serviceOrders[index]?.id}</i> : null}</span>)}</div>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

function RentalPreview({ tenant, mode }: Omit<ProfessionalModulePreviewProps, 'moduleId'>) {
  const [view, setView] = useState<RentalView>('overview');
  const tabs: readonly { readonly id: RentalView; readonly label: string }[] = [
    { id: 'overview', label: 'Übersicht' }, { id: 'orders', label: 'Mietaufträge' },
    { id: 'dispatch', label: 'Disposition' }, { id: 'calendar', label: 'Mietkalender' },
    { id: 'documents', label: 'Dokumente' },
  ];

  return (
    <div className="professional-module">
      <ModuleHeader
        eyebrow="FORQ Mietpark & Disposition"
        title="Mietpark"
        description="Mietgeräte, Reservierungen, Auslieferungen, Rückholungen und Unterlagen in einem Arbeitsbereich."
        tenant={tenant}
        mode={mode}
        actions={<><button type="button"><RefreshCw size={14} /> Aktualisieren</button><button type="button" className="is-primary"><Plus size={14} /> Mietauftrag</button></>}
      />
      <nav className="erp-module-tabs">{tabs.map((tab) => <button type="button" key={tab.id} className={view === tab.id ? 'is-active' : ''} onClick={() => setView(tab.id)}>{tab.label}{tab.id === 'orders' || tab.id === 'dispatch' ? <b>{tab.id === 'orders' ? 3 : 1}</b> : null}</button>)}</nav>

      {view === 'overview' ? (
        <>
          <div className="erp-status-strip"><article><span>Heute verfügbar</span><strong>1</strong><small>von 3 Mietgeräten</small></article><article><span>Aktive Mieten</span><strong>3</strong><small>reserviert oder vermietet</small></article><article><span>Transporte heute</span><strong>1</strong><small>Auslieferung oder Abholung</small></article><article><span>Rücknahmen offen</span><strong>2</strong><small>Prüfung erforderlich</small></article></div>
          <div className="erp-two-column">
            <section className="erp-data-panel"><header><div><span>Nächste Mietvorgänge</span><h2>Was steht als Nächstes an?</h2></div><button type="button">Alle Mietaufträge</button></header>{rentalOrders.map((order) => <article key={order.id}><div><strong>{order.id} · {order.customer}</strong><small>{order.machine}</small></div><span>{order.period}</span><em>{order.status}</em></article>)}</section>
            <section className="erp-data-panel"><header><div><span>Verfügbarkeit heute</span><h2>Mietgeräte am Standort</h2></div><button type="button">Kalender</button></header>{['Toyota 8FBE16T · belegt', 'BT SWE 160 · belegt', 'Kärcher B 60 W · frei'].map((item, index) => <article key={item}><div><strong>{item.split(' · ')[0]}</strong><small>M-{14242 + index} · Werl</small></div><em className={index === 2 ? 'is-ready' : ''}>{item.split(' · ')[1]}</em></article>)}</section>
          </div>
        </>
      ) : null}

      {view === 'orders' ? (
        <section className="erp-data-panel erp-data-panel--full"><header><div><span>Mietaufträge</span><h2>Vorgänge verwalten</h2></div><button type="button" className="is-primary"><Plus size={14} /> Neuer Auftrag</button></header><div className="erp-toolbar"><label><Search size={14} /><input placeholder="Mietnummer, Kunde oder Mietgerät suchen" /></label><button type="button"><Filter size={14} /> Filter</button></div><div className="erp-simple-table"><div className="erp-simple-table__head"><span>Mietauftrag</span><span>Kunde & Gerät</span><span>Zeitraum</span><span>Status</span></div>{rentalOrders.map((order) => <button type="button" key={order.id}><span><strong>{order.id}</strong></span><span><strong>{order.customer}</strong><small>{order.machine}</small></span><span>{order.period}</span><span><em>{order.status}</em></span></button>)}</div></section>
      ) : null}

      {view === 'dispatch' ? (
        <section className="erp-dispatch"><header><div><span>Dispo-Board</span><h2>Auslieferungen & Rückholungen</h2></div></header><div><section><h3><Truck size={15} /> Auslieferung</h3>{rentalOrders.slice(0, 2).map((order) => <article key={order.id}><strong>{order.id}</strong><span>{order.customer} · {order.machine}</span><small>Status: Offen · Fahrer: nicht zugewiesen</small></article>)}</section><section><h3><Truck size={15} /> Abholung</h3>{rentalOrders.slice(1).map((order) => <article key={order.id}><strong>{order.id}</strong><span>{order.customer} · {order.machine}</span><small>Status: Offen · Fahrer: nicht zugewiesen</small></article>)}</section></div></section>
      ) : null}

      {view === 'calendar' ? (
        <section className="erp-calendar"><header><div><span>Mietkalender</span><h2>Verfügbarkeit & Reservierungen</h2></div><div><button type="button">Heute</button><button type="button"><ChevronRight size={14} /></button></div></header><div className="erp-calendar__grid"><div className="erp-calendar__head"><span>Mietgerät</span>{['Di 14', 'Mi 15', 'Do 16', 'Fr 17', 'Sa 18', 'So 19', 'Mo 20', 'Di 21'].map((day) => <span key={day}>{day}</span>)}</div>{['Toyota 8FBE16T', 'BT SWE 160', 'Kärcher B 60 W'].map((machine, index) => <div className="erp-calendar__row" key={machine}><strong>{machine}</strong>{[0, 1, 2, 3, 4, 5, 6, 7].map((day) => <span key={day}>{day >= index && day <= index + 2 ? <i>{rentalOrders[index]?.id}</i> : null}</span>)}</div>)}</div></section>
      ) : null}

      {view === 'documents' ? (
        <section className="erp-data-panel erp-data-panel--full"><header><div><span>Mietdokumente</span><h2>Übergabe, Rücknahme & Unterlagen</h2></div></header><div className="erp-document-grid">{rentalOrders.map((order) => <article key={order.id}><strong>{order.id}</strong><small>{order.customer} · {order.period}</small><div><button type="button">Mietauftrag</button><button type="button">Mietlieferschein</button><button type="button">Auslieferung</button><button type="button">Rücknahme</button></div></article>)}</div></section>
      ) : null}
    </div>
  );
}

function FinancePreview({ tenant, mode }: Omit<ProfessionalModulePreviewProps, 'moduleId'>) {
  const [view, setView] = useState<FinanceView>('cockpit');
  const tabs: readonly { readonly id: FinanceView; readonly label: string }[] = [
    { id: 'cockpit', label: 'Cockpit' }, { id: 'billing', label: 'Abrechnung' }, { id: 'opos', label: 'OPOS' },
    { id: 'incoming', label: 'Eingangsrechnungen' }, { id: 'analytics', label: 'Auswertungen' },
    { id: 'accounting', label: 'Buchhaltung' }, { id: 'closing', label: 'Abschluss' },
  ];
  const title = tabs.find((tab) => tab.id === view)?.label ?? 'Finance';

  return (
    <div className="professional-module">
      <ModuleHeader
        eyebrow="FORQ Finance Center"
        title="Finanzen"
        description="Abrechnung, OPOS, Eingangsrechnungen, Auswertungen und Buchhaltung auf einer gemeinsamen Zahlenbasis."
        tenant={tenant}
        mode={mode}
        actions={<><button type="button"><BarChart3 size={14} /> BWA</button><button type="button" className="is-primary"><FileText size={14} /> Rechnung erstellen</button></>}
      />
      <div className="erp-process-line"><span>Bereit zur Abrechnung</span><ChevronRight size={13} /><span>Rechnung erstellen</span><ChevronRight size={13} /><span>PDF / E-Rechnung</span><ChevronRight size={13} /><span>Versenden</span><ChevronRight size={13} /><span>OPOS & Zahlung</span><ChevronRight size={13} /><span>Auswertung / Export</span></div>
      <nav className="erp-module-tabs">{tabs.map((tab) => <button type="button" key={tab.id} className={view === tab.id ? 'is-active' : ''} onClick={() => setView(tab.id)}>{tab.label}</button>)}</nav>

      <div className="erp-status-strip"><article><span>Bereit zur Abrechnung</span><strong>193,00 €</strong><small>3 Vorgänge</small></article><article><span>Offene Forderungen</span><strong>41.055,00 €</strong><small>5 Debitoren</small></article><article><span>Offene Verbindlichkeiten</span><strong>4.264,05 €</strong><small>3 Kreditoren</small></article><article><span>Operatives Ergebnis</span><strong>55.575,95 €</strong><small>vorläufig</small></article></div>

      <section className="erp-finance-workspace">
        <header><div><span>{title}</span><h2>{view === 'cockpit' ? 'Was muss als Nächstes passieren?' : `${title} bearbeiten`}</h2></div><div><label><Search size={14} /><input placeholder="Beleg, Kunde, Betrag oder Rechnungsnummer" /></label><button type="button"><Filter size={14} /> Filter</button></div></header>
        {view === 'cockpit' ? (
          <div className="erp-two-column"><section className="erp-data-panel"><header><div><span>Arbeitsvorrat</span><h2>Abrechnung und offene Posten</h2></div></header>{['AU-26-000024 · Service / Werkstatt', 'AUF-2026-00001 · Maschinenverkauf', 'MI-26-000002 · Mietvorgang'].map((item, index) => <article key={item}><div><strong>{item}</strong><small>{index === 0 ? 'Rechnungsvorschlag vollständig' : 'Prüfung erforderlich'}</small></div><span>{[193, 27500, 1840][index].toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}</span><em>{index === 0 ? 'Rechnung' : 'Prüfen'}</em></article>)}</section><section className="erp-data-panel"><header><div><span>1-Klick-Ausgaben</span><h2>Listen und Auswertungen</h2></div></header>{['OPOS-Liste', 'BWA / Zeitraum', 'DATEV-Export', 'Zahlungslauf'].map((item) => <article key={item}><div><strong>{item}</strong><small>Aktuelle Mandantendaten</small></div><button type="button">Öffnen</button></article>)}</section></div>
        ) : (
          <div className="erp-simple-table erp-simple-table--finance"><div className="erp-simple-table__head"><span>Beleg</span><span>Geschäftspartner</span><span>Datum</span><span>Status</span><span>Betrag</span></div>{['RE-2026-00128', 'RE-2026-00127', 'ER-2026-00411', 'RE-2026-00119'].map((number, index) => <button type="button" key={number}><span><strong>{number}</strong></span><span>{['Werl Industriebedarf GmbH', 'Müller Logistik GmbH', 'TVH Parts NV', 'Muster Druck GmbH'][index]}</span><span>{['14.07.2026', '13.07.2026', '12.07.2026', '08.07.2026'][index]}</span><span><em>{['Entwurf', 'Offen', 'Freigegeben', 'Überfällig'][index]}</em></span><span>{[193, 27500, 1248.67, 918.4][index].toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}</span></button>)}</div>
        )}
      </section>
    </div>
  );
}

export function isProfessionalPreviewModule(moduleId: string): boolean {
  return moduleId === 'service' || moduleId === 'rental' || moduleId === 'finance';
}

export function ProfessionalModulePreview({ moduleId, tenant, mode }: ProfessionalModulePreviewProps) {
  const props = useMemo(() => ({ tenant, mode }), [mode, tenant]);
  if (moduleId === 'service') return <ServicePreview {...props} />;
  if (moduleId === 'rental') return <RentalPreview {...props} />;
  if (moduleId === 'finance') return <FinancePreview {...props} />;
  return null;
}
